import { useActiveOrganization } from "@/context/ActiveOrganizationProvider";
import { useLlamaPageContext } from "@/context/LlamaPageContext";
import { useMonitoringFile } from "@/context/MonitoringFileContext";
import { ContainsButLooksLikeIsOperator, Filter } from "@/lib/filtering/filters";
import { fileHasPage, getFileHref, useAppHref } from "@/lib/path-utils";
import {
  COST_EVALUATOR_NAME,
  EvaluatorWithAggregates,
  getEvaluatorWithAggregates,
  LATENCY_EVALUATOR_NAME,
  TOKENS_EVALUATOR_NAME,
} from "@/services/evaluations.service";
import { capitalizedFileType, MonitoringEvaluator, refetchVersionsForFile } from "@/services/files.service";
import { File } from "@/types/app/file";
import Button from "@components/library/Button";
import Drawer, { DrawerProps } from "@components/library/Drawer";
import IconButton from "@components/library/IconButton";
import { ArrowRightIcon } from "@components/library/Icons";
import { PageSectionHeader } from "@components/library/PageSectionHeader";
import Tabs from "@components/library/Tabs/Tabs";
import { Tooltip } from "@components/library/Tooltip";
import { DotsVerticalIcon } from "@heroicons/react/outline";
import { isEmpty } from "lodash";
import { useRouter } from "next/router";
import { parseAsString, useQueryState } from "nuqs";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import VersionDetails from "../Configs/VersionDetails";
import { ActiveDeploymentDeployDialog } from "../Dashboard/ActiveDeploymentDeployDialog";
import { DIFF_QUERY_MODIFIED, DIFF_QUERY_ORIGINAL, VersionDiffModal } from "../Dashboard/VersionDiffModal";
import { DatapointsExportDialog } from "../Datasets/DatapointsExportDialog";
import { DebugJsonViewer } from "../DebugJsonViewer";
import { DescriptionDetails, DescriptionList, DescriptionTerm } from "../DescriptionList";
import { DataDisplay, DataDisplayDate } from "../Drawers/DataDisplay";
import { DrawerTitle } from "../Drawers/DrawerTitle";
import { renderAggregateValue } from "../Evaluations/EvaluationResult";
import { UpDownPaginationButtons } from "../PaginationButtons";
import ActionsMenu, { Action } from "../Tables/ActionsColumn/ActionsMenu";
import { useVersionTableActions } from "../Tables/Versions/actions";
import VersionDrawerHeader from "./VersionDrawerHeader";

interface OwnProps {
  version: File | null;
  monitoringEvaluators?: MonitoringEvaluator[];
  open: boolean;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  actions?: Action<File>[];
}
type Props = OwnProps & DrawerProps;

export const VersionPerformance = ({
  version,
  evaluationResults,
}: {
  version: File;
  // TODO: hoping this is not long lived! I just want a simple type here.
  evaluationResults: EvaluatorWithAggregates[];
}) => {
  const { setFilters, filters } = useLlamaPageContext();
  const router = useRouter();
  const { file } = useMonitoringFile();
  const logsHref = useAppHref({
    type: "file",
    id: file?.id ?? "",
    page: "logs",
  });

  const cost = evaluationResults.find(
    (evaluator) => evaluator.monitoringEvaluator.version?.name === COST_EVALUATOR_NAME,
  )?.aggregate;
  const tokens = evaluationResults.find(
    (evaluator) => evaluator.monitoringEvaluator.version?.name === TOKENS_EVALUATOR_NAME,
  )?.aggregate;
  const latency = evaluationResults.find(
    (evaluator) => evaluator.monitoringEvaluator.version?.name === LATENCY_EVALUATOR_NAME,
  )?.aggregate;

  return (
    <>
      <PageSectionHeader title="Summary" size="sm" />
      <div className="grid grid-cols-4 gap-y-20">
        <DataDisplay
          label="Logs"
          value={
            <div className="flex items-center gap-6">
              <span className="tabular-nums">
                {"version_logs_count" in version ? version.version_logs_count : version.datapoints_count}
              </span>
              <Tooltip content="View Logs">
                <IconButton
                  onClick={() => {
                    const newFilter: Filter = {
                      target: {
                        target: "version_id",
                        type: "categorical_multi",
                        label: "Version ID",
                        errorState: false,
                      },
                      operator: ContainsButLooksLikeIsOperator,
                      type: "categorical_multi" as const,
                      value: [version.version_id],
                    };
                    setFilters([...filters, newFilter]);
                    router.push(logsHref);
                  }}
                  shade="gray"
                  Icon={ArrowRightIcon}
                  size={20}
                />
              </Tooltip>
            </div>
          }
        />

        {file?.type === "prompt" ? (
          <>
            <DataDisplay label="Tokens (avg.)" value={tokens?.value ? `${tokens.value?.toFixed(0)}` : "N/A"} />
            <DataDisplay label="Cost per 1k" value={cost?.value ? `$ ${(cost.value * 1000)?.toFixed(2)}` : "N/A"} />
            <DataDisplay
              label="Latency (avg.)"
              value={latency?.value ? `${(latency.value * 1000).toFixed(0)} ms` : "N/A"}
            />
          </>
        ) : null}

        <DataDisplayDate label="Last used" datetime={version.last_used_at} />
      </div>
    </>
  );
};

const VersionDrawer = ({ version, monitoringEvaluators, open, onClose, onNext, onPrevious, ...props }: Props) => {
  const { slug } = useActiveOrganization();
  const router = useRouter();
  const { filters, setFilters } = useLlamaPageContext();
  const [versionForDeployment, setVersionForDeployment] = useState<File | null>(null);
  const [versionForExport, setVersionForExport] = useState<File | null>(null);
  const [originalVersionId, setOriginalVersionId] = useQueryState(DIFF_QUERY_ORIGINAL, parseAsString);
  const [modifiedVersionId, setModifiedVersionId] = useQueryState(DIFF_QUERY_MODIFIED, parseAsString);

  const actions = useVersionTableActions(version ?? undefined, setVersionForDeployment, setVersionForExport);

  const { defaultEvals, customEvals } = getEvaluatorWithAggregates(
    version && "evaluator_aggregates" in version ? (version.evaluator_aggregates ?? []) : [],
    monitoringEvaluators,
  );

  useHotkeys(
    "e",
    (e) => {
      e.preventDefault();
      if (version && fileHasPage(version, "editor")) {
        router.push(`${getFileHref({ slug, id: version?.id ?? "", page: "editor" })}/${version?.version_id}`);
      }
    },
    { enabled: open },
    [open, router],
  );

  const logsHref = useAppHref({
    type: "file",
    id: version?.id ?? "",
    page: "logs",
  });

  useHotkeys(
    "l",
    (e) => {
      if (version && fileHasPage(version, "logs")) {
        router.push(logsHref);
        const newFilter: Filter = {
          target: {
            target: "version_id",
            type: "categorical_multi",
            label: "Version ID",
            errorState: false,
          },
          operator: ContainsButLooksLikeIsOperator,
          type: "categorical_multi" as const,
          value: [version?.version_id ?? ""],
        };
        setFilters([...filters, newFilter]);
        router.push(logsHref);
      }
    },
    { enabled: open },
    [open, router],
  );

  return (
    <>
      <Drawer
        open={open && version !== null}
        onClose={onClose}
        id={version?.version_id}
        title={
          version && (
            <DrawerTitle
              title={capitalizedFileType(version.type) + " Version"}
              entityType={version.type}
              entityId={version.version_id}
              copyText="Version ID"
              // Always link to the dashboard version page, as this is the only place that allows for deep linking to version drawers.
              linkToCopy={`${window.location.origin}${getFileHref({ slug, id: version.id, page: "dashboard" })}?id=${version.version_id}`}
              copyLinkTooltip="Copy Link to Version"
            />
          )
        }
        extra={
          version?.version_id !== undefined ? (
            <div className="flex items-center justify-end gap-12 pr-52">
              <ActionsMenu
                actions={actions ?? []}
                data={version}
                trigger={
                  <Button size={24} IconRight={DotsVerticalIcon}>
                    Actions
                  </Button>
                }
              />
              <div className="h-24 border-l border-stroke-base-3" />
              <UpDownPaginationButtons handlePrev={onPrevious} handleNext={onNext} />
            </div>
          ) : null
        }
        {...props}
      >
        {version && (
          <div className="p-24">
            <div className="flex  w-full items-start justify-between gap-12">
              <VersionDrawerHeader fileId={version.id} versionId={version.version_id} />
            </div>
            <div className="h-20" />
            <Tabs.Group as="div" className="">
              <Tabs.List>
                <Tabs.Tab>Details</Tabs.Tab>
                {version?.type !== "dataset" && <Tabs.Tab>Performance</Tabs.Tab>}
                {version?.type === "prompt" && <Tabs.Tab>Schema</Tabs.Tab>}
                <Tabs.Tab>JSON</Tabs.Tab>
              </Tabs.List>
              <Tabs.Panels className="">
                <Tabs.Panel className="py-24">
                  <VersionDetails file={version} />
                </Tabs.Panel>

                {version?.type !== "dataset" && (
                  <Tabs.Panel className="py-24">
                    <div className="flex flex-col">
                      <VersionPerformance version={version} evaluationResults={defaultEvals} />
                      <div className="h-32" />
                      <PageSectionHeader title="Evaluations" size="sm" />
                      <DescriptionList>
                        {customEvals?.map((evaluationAggregate) => {
                          const activatedEvaluatorVersion = evaluationAggregate.monitoringEvaluator.version;
                          if (!activatedEvaluatorVersion) return null;
                          const evaluatorName = activatedEvaluatorVersion.name;
                          return (
                            <>
                              <DescriptionTerm>{evaluatorName}</DescriptionTerm>
                              <DescriptionDetails>
                                {
                                  renderAggregateValue(activatedEvaluatorVersion, evaluationAggregate.aggregate.value)
                                    .displayValue
                                }
                              </DescriptionDetails>
                            </>
                          );
                        })}
                      </DescriptionList>
                      {isEmpty(customEvals) && (
                        <div className="flex h-160 w-full flex-col items-center justify-center gap-8 rounded-lg border border-stroke-base-2 ">
                          <div className="text-14-20 font-bold">No data</div>
                          <span className="text-12-16">No Logs have been evaluated yet</span>
                        </div>
                      )}
                    </div>
                  </Tabs.Panel>
                )}
                {/* Schema */}
                {version?.type === "prompt" && (
                  <Tabs.Panel className="py-24">
                    <PageSectionHeader title="Schema" description="JSON Schema for the Prompt" size="sm" />
                    <DebugJsonViewer value={version.schema} />
                  </Tabs.Panel>
                )}
                {/* JSON */}
                <Tabs.Panel className="py-24">
                  <PageSectionHeader
                    title={`${version ? capitalizedFileType(version?.type) : "Full"} Object`}
                    size="sm"
                  />
                  <DebugJsonViewer value={version} />
                </Tabs.Panel>
              </Tabs.Panels>
            </Tabs.Group>
          </div>
        )}
      </Drawer>

      <ActiveDeploymentDeployDialog
        version={versionForDeployment}
        open={versionForDeployment !== null}
        onClose={() => setVersionForDeployment(null)}
        onDeploy={() => {
          refetchVersionsForFile(version?.id ?? "");
        }}
      />
      <DatapointsExportDialog
        open={versionForExport !== null}
        onClose={() => setVersionForExport(null)}
        datapointsCount={
          versionForExport && "datapoints_count" in versionForExport ? versionForExport.datapoints_count : 0
        }
        versionId={versionForExport?.version_id}
      />

      <VersionDiffModal
        open={originalVersionId !== null && modifiedVersionId !== null}
        onClose={() => {
          setOriginalVersionId(null);
          setModifiedVersionId(null);
        }}
      />
    </>
  );
};

export default VersionDrawer;
